/**
 * OpcionesManager.js
 *
 * Catálogo de opciones de plato (pedido de Angelo, 2026-08-09). Vive dentro de
 * Inventario como una pestaña.
 *
 * Dos pantallas en una:
 *  - Grupos: "Proteínas", "Término de la carne", "Acompañamiento". Cada uno con
 *    su mín/máx por defecto y sus opciones.
 *  - Disponibilidad: el interruptor de "hoy no hay papa frita". Está arriba de
 *    todo a propósito — es lo que se toca en medio del servicio, y se apaga una
 *    vez para todos los platos que usan esa opción, no plato por plato.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { Plus, Trash2, Pencil, Check, X, AlertTriangle, Layers } from 'lucide-react';
import CustomAlert from './shared/CustomAlert';

const OpcionesManager = ({ token }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newItemFor, setNewItemFor] = useState(null);
  const [newItemName, setNewItemName] = useState('');
  const [newItemDelta, setNewItemDelta] = useState('0');

  const H = { headers: { Authorization: `Bearer ${token}` } };
  const showAlert = (type, title, message) => setAlert({ type, title, message });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/pos/option-groups`, H);
      setGroups(res.data || []);
    } catch (e) {
      showAlert('error', 'Error', 'No se pudieron cargar las opciones.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  /* ---------------- grupos ---------------- */

  const createGroup = async (data) => {
    try {
      await axios.post(`${API_URL}/api/pos/option-groups`, data, H);
      setShowNewGroup(false);
      load();
    } catch (e) {
      showAlert('error', 'Error', e.response?.data?.error || 'No se pudo crear el grupo.');
    }
  };

  const saveGroup = async (id, data) => {
    try {
      await axios.put(`${API_URL}/api/pos/option-groups/${id}`, data, H);
      setEditingGroup(null);
      load();
    } catch (e) {
      showAlert('error', 'Error', e.response?.data?.error || 'No se pudo guardar.');
    }
  };

  const deleteGroup = (g) => {
    setAlert({
      type: 'confirm',
      title: 'Eliminar grupo',
      message: `¿Eliminar "${g.name}" y sus ${g.items.length} opciones?\n\nLos platos que lo usaban dejan de pedirlo. No se puede deshacer.`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/pos/option-groups/${g.id}`, H);
          load();
        } catch (e) {
          showAlert('error', 'Error', 'No se pudo eliminar.');
        }
      }
    });
  };

  /* ---------------- opciones ---------------- */

  const addItem = async (groupId) => {
    if (!newItemName.trim()) return;
    try {
      await axios.post(`${API_URL}/api/pos/option-groups/${groupId}/items`,
        { name: newItemName.trim(), price_delta: Number(newItemDelta) || 0 }, H);
      setNewItemName('');
      setNewItemDelta('0');
      setNewItemFor(null);
      load();
    } catch (e) {
      showAlert('error', 'Error', e.response?.data?.error || 'No se pudo agregar la opción.');
    }
  };

  const toggleAvailability = async (item) => {
    // Optimista: el cocinero está en medio del servicio, esperar el round-trip
    // para ver el cambio hace que lo toque dos veces.
    setGroups(gs => gs.map(g => ({
      ...g,
      items: g.items.map(i => (i.id === item.id ? { ...i, is_available: !i.is_available } : i))
    })));
    try {
      await axios.post(`${API_URL}/api/pos/option-items/${item.id}/availability`,
        { is_available: !item.is_available }, H);
    } catch (e) {
      showAlert('error', 'Error', 'No se pudo cambiar la disponibilidad.');
      load();
    }
  };

  const deleteItem = async (item) => {
    try {
      await axios.delete(`${API_URL}/api/pos/option-items/${item.id}`, H);
      load();
    } catch (e) {
      showAlert('error', 'Error', 'No se pudo eliminar.');
    }
  };

  const unavailable = groups.flatMap(g => g.items.filter(i => !i.is_available).map(i => ({ ...i, groupName: g.name })));

  if (loading) return <div className="p-8 text-center text-gray-500">Cargando opciones...</div>;

  return (
    <div className="p-4 space-y-4">
      {alert && <CustomAlert {...alert} onClose={() => setAlert(null)} />}

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-600" /> Opciones de platos
          </h3>
          <p className="text-sm text-gray-500 max-w-2xl">
            Grupos reutilizables para platos que se arman al momento: proteínas, término de la carne,
            acompañamiento. Se definen una vez acá y después cada plato elige cuáles usa y cuántas
            opciones exige.
          </p>
        </div>
        <button onClick={() => setShowNewGroup(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Nuevo grupo
        </button>
      </div>

      {/* Agotados de hoy — arriba porque es lo que se toca durante el servicio */}
      {unavailable.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> Marcado como no disponible ahora mismo
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {unavailable.map(i => (
              <button key={i.id} onClick={() => toggleAvailability(i)}
                className="text-xs bg-white border border-amber-300 text-amber-800 px-2.5 py-1 rounded-full hover:bg-amber-100"
                title="Volver a habilitar">
                {i.groupName}: <b>{i.name}</b> · reactivar
              </button>
            ))}
          </div>
          <p className="text-[11px] text-amber-700 mt-2">
            Los platos que usan estas opciones se siguen vendiendo; solo desaparece la opción agotada.
            Si un plato se queda sin ninguna opción disponible en un grupo obligatorio, la caja avisa.
          </p>
        </div>
      )}

      {showNewGroup && (
        <GroupForm
          onCancel={() => setShowNewGroup(false)}
          onSave={createGroup}
        />
      )}

      {groups.length === 0 && !showNewGroup && (
        <div className="text-center py-12 bg-gray-50 border border-dashed rounded-lg">
          <p className="text-gray-600 font-semibold">Todavía no hay grupos de opciones</p>
          <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
            Ejemplo: un grupo "Proteínas" con carne, pollo, chorizo y chuleta. Después, en la menestra
            doble eliges ese grupo y pones que exige 2.
          </p>
          <button onClick={() => setShowNewGroup(true)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold text-sm">
            Crear el primero
          </button>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {groups.map(g => (
          <div key={g.id} className={`bg-white border rounded-xl p-4 ${g.active ? '' : 'opacity-60'}`}>
            {editingGroup === g.id ? (
              <GroupForm group={g} onCancel={() => setEditingGroup(null)} onSave={(d) => saveGroup(g.id, d)} />
            ) : (
              <>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-gray-800">{g.name}</p>
                    {g.description && <p className="text-xs text-gray-500">{g.description}</p>}
                    <p className="text-[11px] text-blue-700 mt-0.5">
                      {g.min_select === g.max_select
                        ? `Elige exactamente ${g.min_select}`
                        : `Elige de ${g.min_select} a ${g.max_select}`}
                      {g.min_select === 0 && ' (opcional)'}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => setEditingGroup(g.id)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => deleteGroup(g)} className="p-1.5 text-gray-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                <div className="mt-3 space-y-1.5">
                  {g.items.length === 0 && <p className="text-xs text-gray-400">Sin opciones todavía.</p>}
                  {g.items.map(i => (
                    <div key={i.id} className="flex items-center gap-2 text-sm">
                      <button onClick={() => toggleAvailability(i)}
                        title={i.is_available ? 'Marcar como agotado hoy' : 'Volver a habilitar'}
                        className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${i.is_available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {i.is_available ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      </button>
                      <span className={`flex-1 ${i.is_available ? 'text-gray-700' : 'text-gray-400 line-through'}`}>{i.name}</span>
                      {i.price_delta !== 0 && (
                        <span className="text-xs font-semibold text-gray-500">
                          {i.price_delta > 0 ? '+' : ''}${i.price_delta.toFixed(2)}
                        </span>
                      )}
                      <button onClick={() => deleteItem(i)} className="p-1 text-gray-300 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>

                {newItemFor === g.id ? (
                  <div className="flex gap-1.5 mt-2">
                    <input autoFocus value={newItemName} onChange={e => setNewItemName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addItem(g.id); if (e.key === 'Escape') setNewItemFor(null); }}
                      placeholder="Nombre de la opción" className="flex-1 px-2 py-1 border rounded text-sm" />
                    <input value={newItemDelta} onChange={e => setNewItemDelta(e.target.value)}
                      type="number" step="0.01" title="Recargo (0 si no cambia el precio)"
                      className="w-20 px-2 py-1 border rounded text-sm" />
                    <button onClick={() => addItem(g.id)} className="px-2 py-1 bg-blue-600 text-white rounded text-xs font-semibold">OK</button>
                  </div>
                ) : (
                  <button onClick={() => { setNewItemFor(g.id); setNewItemName(''); setNewItemDelta('0'); }}
                    className="mt-2 text-xs text-blue-600 font-semibold hover:underline">+ Agregar opción</button>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */

function GroupForm({ group, onCancel, onSave }) {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [min, setMin] = useState(String(group?.min_select ?? 1));
  const [max, setMax] = useState(String(group?.max_select ?? 1));

  const minN = Number(min) || 0;
  const maxN = Number(max) || 1;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
      <input autoFocus value={name} onChange={e => setName(e.target.value)}
        placeholder="Nombre del grupo (ej: Proteínas)" className="w-full px-2 py-1.5 border rounded text-sm" />
      <input value={description} onChange={e => setDescription(e.target.value)}
        placeholder="Descripción opcional (se muestra al cajero)" className="w-full px-2 py-1.5 border rounded text-sm" />
      <div className="flex gap-2 items-end">
        <label className="flex-1 text-xs text-gray-600">
          Mínimo
          <input type="number" min="0" max="20" value={min} onChange={e => setMin(e.target.value)}
            className="w-full px-2 py-1 border rounded text-sm mt-0.5" />
        </label>
        <label className="flex-1 text-xs text-gray-600">
          Máximo
          <input type="number" min="1" max="20" value={max} onChange={e => setMax(e.target.value)}
            className="w-full px-2 py-1 border rounded text-sm mt-0.5" />
        </label>
      </div>
      <p className="text-[11px] text-gray-600">
        {minN === 0
          ? 'Opcional: el cajero puede no elegir nada.'
          : minN === maxN
            ? `El cajero debe elegir exactamente ${minN}.`
            : `El cajero debe elegir entre ${minN} y ${maxN}.`}
        {' '}Cada plato puede pisar estos números.
      </p>
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 py-1.5 bg-white border rounded text-sm font-semibold text-gray-600">Cancelar</button>
        <button
          onClick={() => name.trim() && onSave({ name: name.trim(), description, min_select: minN, max_select: maxN })}
          className="flex-1 py-1.5 bg-blue-600 text-white rounded text-sm font-semibold">
          Guardar
        </button>
      </div>
    </div>
  );
}

export default OpcionesManager;
